import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Sparkles, TrendingUp, FileText, BarChart3, Brain, Zap } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';

const AITools = () => {
  const navigate = useNavigate();

  const upcomingTools = [
    {
      id: 1,
      name: "AI Tax Advisor",
      icon: Brain,
      description: "Get personalized tax-saving recommendations based on your financial profile",
      status: "In Development"
    },
    {
      id: 2,
      name: "Smart Compliance Tracker",
      icon: FileText,
      description: "Automated compliance deadline tracking with intelligent reminders",
      status: "Coming Soon"
    },
    {
      id: 3,
      name: "Financial Insights Dashboard",
      icon: BarChart3,
      description: "Real-time financial analytics and business health monitoring",
      status: "Coming Soon"
    },
    {
      id: 4,
      name: "Intelligent Document Scanner",
      icon: Zap,
      description: "AI-powered extraction of data from receipts, invoices, and financial documents",
      status: "Coming Soon"
    },
    {
      id: 5,
      name: "Predictive Cash Flow",
      icon: TrendingUp,
      description: "Forecast your cash flow with AI-powered predictive analytics",
      status: "Coming Soon"
    },
    {
      id: 6,
      name: "Automated GST Reconciliation",
      icon: Sparkles,
      description: "AI-driven GST return preparation and reconciliation",
      status: "Coming Soon"
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-purple-50 via-blue-50 to-white">
      {/* Header */}
      <header className="bg-white border-b sticky top-0 z-10">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            <Button
              variant="ghost"
              onClick={() => navigate('/')}
              className="flex items-center gap-2"
            >
              <ArrowLeft className="w-5 h-5" />
              Back to Home
            </Button>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="container mx-auto">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-block p-4 bg-gradient-to-br from-purple-100 to-blue-100 rounded-full mb-6">
              <svg className="w-20 h-20 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
              </svg>
            </div>
            
            <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-6">
              AI-Powered Financial Tools
            </h1>
            
            <p className="text-xl text-gray-600 mb-8">
              Revolutionary AI tools to simplify your financial management and compliance
            </p>
            
            <div className="inline-flex items-center gap-3 bg-gradient-to-r from-purple-600 to-blue-600 text-white px-8 py-4 rounded-full text-lg font-semibold mb-8">
              <Sparkles className="w-6 h-6" />
              <span>Coming Soon - 2026</span>
            </div>

            <p className="text-gray-700 max-w-2xl mx-auto">
              We're building cutting-edge AI-powered tools to revolutionize how you manage your finances, 
              taxes, and compliance. These tools will save you time, reduce errors, and provide intelligent 
              insights for better financial decisions.
            </p>
          </div>
        </div>
      </section>

      {/* Upcoming Tools */}
      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="container mx-auto">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl font-bold text-center text-gray-900 mb-4">
              What We're Building
            </h2>
            <p className="text-center text-gray-600 mb-12 max-w-2xl mx-auto">
              A comprehensive suite of AI tools designed specifically for Indian businesses and taxpayers
            </p>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {upcomingTools.map((tool) => {
                const Icon = tool.icon;
                return (
                  <Card key={tool.id} className="group hover:shadow-xl transition-all duration-300 border-2 hover:border-purple-300">
                    <CardHeader>
                      <div className="w-14 h-14 bg-gradient-to-br from-purple-500 to-blue-500 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                        <Icon className="w-7 h-7 text-white" />
                      </div>
                      <CardTitle className="text-xl">{tool.name}</CardTitle>
                      <div className="inline-block">
                        <span className="px-3 py-1 bg-purple-100 text-purple-700 text-xs font-semibold rounded-full">
                          {tool.status}
                        </span>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <CardDescription className="text-gray-600">
                        {tool.description}
                      </CardDescription>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Notify Me Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="container mx-auto">
          <div className="max-w-3xl mx-auto bg-white rounded-2xl shadow-xl p-8 md:p-12 text-center">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Be the First to Know
            </h2>
            <p className="text-lg text-gray-600 mb-8">
              Get notified when we launch these revolutionary AI tools. Join the waitlist today!
            </p>
            <Button
              size="lg"
              onClick={() => {
                navigate('/');
                setTimeout(() => {
                  document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' });
                }, 100);
              }}
              className="bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white text-lg h-14 px-8"
            >
              Join Waitlist
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AITools;
